// FillApp shared types + pure helpers (used by student form pages).

// The application cycle to fall back on when a caller has no student payload
// to read the real one from. ONE place to bump at the annual rollover — the
// server's StudentFillApp.cycleYear default is the other. Anywhere a student's
// own cycleYear is available, pass it instead of relying on this.
export const DEFAULT_CYCLE_YEAR = 2026;


export type FillAppLabelLink = { text: string; href: string };
export type FillAppLabelBlock =
    | { type: 'p'; text: string }
    | { type: 'ul'; items: string[] };

export type FillAppField = {
    qid: string;
    label: string;
    type: string | null;
    required: boolean;
    sensitive?: boolean;            // never collected (SSN etc.): disabled UI, blocked writes, uncounted
    hiddenByDefault?: boolean;
    maxLength?: number | null;
    choiceGroup?: string;
    min?: number | string | null;
    max?: number | string | null;
    showWhen?: ShowWhenRule;
    labelLinks?: FillAppLabelLink[];
    // Paragraph/bullet structure, present only when the label contains a list
    // (e.g. the household independence criteria). Falls back to `label`.
    labelBlocks?: FillAppLabelBlock[];
    hint?: string;
    // Options narrowed by another answer (e.g. course schedule terms by the
    // selected scheduling system): map[triggerValue] = allowed exportCodes.
    optionsFilter?: { qid: string; map: Record<string, string[]> };
    // Bold label prefix: block sub-header ("Counselor/Advisor/Mentor") or
    // inline lead ("International applicants:").
    boldLead?: { text: string; block: boolean };
};

export type ShowWhenClause =
    | { qid: string; equals: string }
    | { qid: string; isSet: true }
    | { qid: string; includes: string }
    | { qid: string; in: string[] };

export type ShowWhenRule =
    | ShowWhenClause
    | { all: ShowWhenClause[] }
    | { any: ShowWhenClause[] };

export type FillAppRepeatingGroup = {
    id: string;
    label: string;                  // singular noun: "activity", "language", "military experience"
    mode: 'count' | 'button';
    countQid?: string;              // count mode: the "Number of X" dropdown qid
    maxSlots: number;
    softMax?: number;               // real Common App cap; extra slots are drafting spares
    minSlots: number;               // entries shown once the group is in view
    gate?: ShowWhenRule;            // whole-group visibility (button mode)
    sharedQids: string[];           // rendered once above the entries
    trailingSharedQids: string[];   // rendered once below the entries
    slots: string[][];              // slots[i] = qids of entry i
};


// A card group: a contiguous run of related questions rendered as one
// collapsible card in the form UI. Stamped by scripts/add_groups.py.
export type FillAppFieldGroup = {
    id: string;
    title: string;
    qids: string[];
};

export type FillAppSubtab = {
    id: string;
    label: string;             // short tab/sidebar label
    title?: string;            // full page heading (live Common App h1), falls back to label
    fields: FillAppField[];
    repeatingGroups?: FillAppRepeatingGroup[];
    groups?: FillAppFieldGroup[];   // card groups for the summary-card form UI
    showWhen?: ShowWhenRule;   // whole-section gate (e.g. step-parents)
};

export type FillAppScreen = {
    id: string;
    label: string;
    subtabs: FillAppSubtab[];
};

export type ChoiceOption = { order: string | number; label: string; exportCode: string };
export type ChoiceGroups = Record<string, ChoiceOption[]>;

export type FillAppSchema = {
    cycleYear: number;
    screens: FillAppScreen[];
    choiceGroups: ChoiceGroups;
};

export type FillAppValues = Record<string, unknown>;

// ---- Courses & Grades transcript widget (type 'CoursesGrades') ---------------

export type CGCourse = {
    subject: string;
    name: string;
    level: string;
    grades: Record<string, string>;    // keyed by schedule column (S1, T2, Final…)
    credits: Record<string, string>;
    creditsNA: boolean;
};

export type CGTranscript = {
    school: string;
    year: string;
    scale: string;
    scaleOther: string;
    schedule: string;
    scheduleOther: string;
    courses: CGCourse[];
};

export type CoursesGradesValue = { transcripts: CGTranscript[] };

// Grade/credit columns per scheduling system (from the Common App captures).
export const CG_SCHEDULE_COLUMNS: Record<string, string[]> = {
    semesters: ['S1', 'S2', 'Final'],
    trimesters: ['T1', 'T2', 'T3', 'Final'],
    quarters: ['Q1', 'Q2', 'Q3', 'Q4', 'Final'],
    yearly: ['Final'],
    other: ['Final'],
};

export const CG_SCHEDULE_LABELS: Record<string, string> = {
    semesters: 'Semesters', trimesters: 'Trimesters', quarters: 'Quarters',
    yearly: 'Yearly', other: 'Other',
};

// Grading-scale code → display label (codes match the Common App grade map).
export const CG_SCALE_LABELS: Record<string, string> = {
    '0': 'A+ to F (Letter)', '1': '0 to 100', '2': '0 to 4.0', '3': '0 to 5.0',
    '4': '1 to 7', '5': '0 to 10', '6': '0 to 11', '7': '0 to 20', other: 'Other',
};

export function parseCoursesGrades(value: unknown): CoursesGradesValue | null {
    if (value && typeof value === 'object' && !Array.isArray(value)
        && Array.isArray((value as CoursesGradesValue).transcripts)) {
        return value as CoursesGradesValue;
    }
    return null;
}

function cgCourseEmpty(c: CGCourse): boolean {
    return !c.subject && !c.name && !c.level && !c.creditsNA
        && Object.values(c.grades || {}).every(g => !g)
        && Object.values(c.credits || {}).every(g => !g);
}

function cgCourseComplete(c: CGCourse, cols: string[]): boolean {
    if (!c.subject || !c.name || !c.level || cols.length === 0) return false;
    if (!cols.every(col => (c.grades[col] || '').trim() !== '')) return false;
    return c.creditsNA || cols.every(col => (c.credits[col] || '').trim() !== '');
}

// True if the widget holds at least one started-but-unfinished course. Such a
// section must NOT be marked complete even with the confirmation box checked.
export function coursesGradesHasIncomplete(value: unknown): boolean {
    const data = parseCoursesGrades(value);
    if (!data) return false;
    return data.transcripts.some(t => {
        const cols = CG_SCHEDULE_COLUMNS[t.schedule] ?? [];
        return t.courses.some(c => !cgCourseEmpty(c) && !cgCourseComplete(c, cols));
    });
}
export type SectionStatus = Record<string, 'not_started' | 'in_progress' | 'complete'>;

function resolveClause(c: ShowWhenClause, values: FillAppValues): boolean {
    const v = values[c.qid];
    if ('isSet' in c) {
        if (v == null) return false;
        if (typeof v === 'string') return v.trim() !== '';
        if (Array.isArray(v)) return v.length > 0;
        return true;
    }
    if ('includes' in c) {
        // Checkbox values are stored as arrays of exportCodes (or a
        // comma-joined string fallback).
        if (Array.isArray(v)) return v.map(String).includes(c.includes);
        if (typeof v === 'string') return v.split(',').includes(c.includes);
        return false;
    }
    if ('in' in c) return c.in.includes(String(v ?? ''));
    return String(v ?? '') === c.equals;
}

export function resolveShowWhen(rule: ShowWhenRule | undefined, values: FillAppValues): boolean {
    if (!rule) return true;
    if ('any' in rule) return rule.any.some(c => resolveClause(c, values));
    const clauses: ShowWhenClause[] = 'all' in rule ? rule.all : [rule];
    return clauses.every(c => resolveClause(c, values));
}

export function choiceLabel(group: ChoiceOption[] | undefined, rawValue: unknown): string {
    if (!group || rawValue == null || rawValue === '') return '';
    const v = String(rawValue);
    const match = group.find(o => String(o.exportCode) === v || String(o.order) === v || o.label === v);
    return match ? match.label : v;
}

// ---- Input limits --------------------------------------------------------

// Common App word limits for the essay-style textareas. These live in Common
// App's JS, not its markup, so the schema capture missed them — maintained
// here by qid.
export const WORD_LIMITS: Record<string, number> = {
    q_832: 650,   // Writing: personal essay
    q_1956: 250,  // Writing: challenges / community disruption
    q_835: 300,   // Writing: additional information
    q_1932: 250,  // Education: did/will not graduate details
    q_836: 250,   // Education: change in progression details
    q_1931: 250,  // Education: why left other secondary schools
};

// Word minimums (the live form shows "Min: 250 / Max: 650" on the personal
// essay). A field with a minimum doesn't count as filled until it's met.
export const WORD_MINIMUMS: Record<string, number> = {
    q_832: 250,   // Writing: personal essay
};

export function wordCount(text: string): number {
    const t = text.trim();
    return t === '' ? 0 : t.split(/\s+/).length;
}

// Character cap for a field. Labels like "(Max characters: 100)" are the
// authoritative source — several captured maxLength values are missing or
// truncated (e.g. "50" captured as 5); otherwise fall back to maxLength.
export function fieldCharLimit(field: FillAppField): number | null {
    const m = /Max characters:\s*(\d+)/i.exec(field.label || '');
    if (m) return parseInt(m[1], 10);
    const n = parseInt(String(field.maxLength ?? ''), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
}

// ---- Repeating-group helpers -------------------------------------------------

export function slotCountKey(group: FillAppRepeatingGroup): string {
    return `__slots_${group.id}`;
}

// How many entry slots are currently active (shown) for a group.
export function groupActiveSlots(group: FillAppRepeatingGroup, values: FillAppValues): number {
    if (group.gate && !resolveShowWhen(group.gate, values)) return 0;
    let n: number;
    if (group.mode === 'count') {
        const raw = values[group.countQid as string];
        n = parseInt(String(raw ?? ''), 10);
        if (Number.isNaN(n)) n = 0;
    } else {
        const raw = values[slotCountKey(group)];
        n = parseInt(String(raw ?? ''), 10);
        if (Number.isNaN(n)) n = group.minSlots;
    }
    return Math.max(0, Math.min(n, group.maxSlots));
}

// All qids belonging to any repeating group in a subtab, and the subset that is
// currently active (shared fields when the group is in view + active slots).
export function partitionGroupQids(
    subtab: FillAppSubtab,
    values: FillAppValues,
): { all: Set<string>; active: Set<string> } {
    const all = new Set<string>();
    const active = new Set<string>();
    for (const group of subtab.repeatingGroups ?? []) {
        const shared = [...group.sharedQids, ...group.trailingSharedQids];
        shared.forEach(q => all.add(q));
        group.slots.forEach(slot => slot.forEach(q => all.add(q)));
        // Leading shared fields always render (the gate/count question lives
        // there — for Activities and Honors the gate qid IS a shared qid), so
        // they are always active; per-field showWhen still hides follow-ups.
        // Gating them on the gate answer made an untouched Activities section
        // read "complete": its only required field was excluded from counting.
        // Trailing shared fields render only once the gate passes.
        const gateOk = !group.gate || resolveShowWhen(group.gate, values);
        group.sharedQids.forEach(q => active.add(q));
        if (gateOk) group.trailingSharedQids.forEach(q => active.add(q));
        const n = groupActiveSlots(group, values);
        for (let i = 0; i < n; i++) group.slots[i]?.forEach(q => active.add(q));
    }
    return { all, active };
}

// ---- Progress helpers --------------------------------------------------------

function isEmpty(v: unknown): boolean {
    if (v == null) return true;
    if (typeof v === 'string') return v.trim() === '';
    if (Array.isArray(v)) return v.length === 0;
    return false;
}

// Does a field count as "filled" for progress/completion? A CoursesGrades
// field with any started-but-unfinished course counts as NOT filled, so its
// section can't reach "complete" until those courses are finished or removed.
// An empty widget ("no courses to report") is fine — the section's separate
// confirmation checkbox is what gates that case.
function fieldIsFilled(field: FillAppField, values: FillAppValues): boolean {
    if (field.type === 'CoursesGrades') {
        return !coursesGradesHasIncomplete(values[field.qid]);
    }
    const min = WORD_MINIMUMS[field.qid];
    const max = WORD_LIMITS[field.qid];
    if (min || max) {
        const words = wordCount(String(values[field.qid] ?? ''));
        if (min && words < min) return false;
        if (max && words > max) return false;
    }
    return !isEmpty(values[field.qid]);
}

// Whole-section gate (step-parents, legal guardian) from household answers.
export function subtabIsVisible(subtab: FillAppSubtab, values: FillAppValues): boolean {
    return !subtab.showWhen || resolveShowWhen(subtab.showWhen, values);
}

export function fieldIsVisible(field: FillAppField, values: FillAppValues): boolean {
    // A field whose options depend on another answer (course schedule ← the
    // scheduling system) hides entirely until that trigger is answered — an
    // empty dropdown would just confuse.
    if (field.optionsFilter && String(values[field.optionsFilter.qid] ?? '').trim() === '') {
        return false;
    }
    if (field.hiddenByDefault) {
        if (!field.showWhen) return false;
        return resolveShowWhen(field.showWhen, values);
    }
    if (field.showWhen) return resolveShowWhen(field.showWhen, values);
    return true;
}

// Fields of a subtab that count toward progress: non-group fields that are
// visible, plus active repeating-group fields that are visible.
function countableFields(subtab: FillAppSubtab, values: FillAppValues): FillAppField[] {
    const { all, active } = partitionGroupQids(subtab, values);
    const out: FillAppField[] = [];
    for (const field of subtab.fields) {
        if (field.type === 'Label') continue; // display-only text, no input
        if (field.sensitive) continue;        // never collected → never counted
        if (all.has(field.qid)) {
            if (!active.has(field.qid)) continue;
            if (!fieldIsVisible(field, values)) continue;
        } else if (!fieldIsVisible(field, values)) {
            continue;
        }
        out.push(field);
    }
    return out;
}

function screenQids(screen: FillAppScreen): string[] {
    const qids: string[] = [];
    for (const s of screen.subtabs) {
        for (const f of s.fields) qids.push(f.qid);
    }
    return qids;
}

// Fields that count toward completion and percent: required ones, plus
// CoursesGrades widgets (not flagged required, but a started-but-unfinished
// course must still hold a section back — see subtabRequiredComplete), plus
// word-minimum fields (the personal essay isn't flagged required in the
// capture, but Common App won't complete the section without a 250-650-word
// essay).
function isCompletionField(field: FillAppField): boolean {
    return field.required || field.type === 'CoursesGrades'
        || WORD_MINIMUMS[field.qid] != null;
}

// Never round an incomplete ratio up to a misleading "100%".
function completionPercent(filled: number, total: number): number {
    if (total === 0) return 100;
    const pct = Math.round((filled / total) * 100);
    return filled < total ? Math.min(pct, 99) : pct;
}

// Progress is over required fields only (Common App semantics — optional
// fields never move the completion numbers). No visible required fields = 100%.
export function computeScreenProgress(
    screen: FillAppScreen,
    values: FillAppValues,
): { filled: number; total: number; percent: number } {
    let filled = 0;
    let total = 0;
    for (const subtab of screen.subtabs) {
        if (!subtabIsVisible(subtab, values)) continue;
        for (const field of countableFields(subtab, values)) {
            if (!isCompletionField(field)) continue;
            total += 1;
            if (fieldIsFilled(field, values)) filled += 1;
        }
    }
    return { filled, total, percent: completionPercent(filled, total) };
}

export function computeSubtabFilled(
    subtab: FillAppSubtab,
    values: FillAppValues,
): { filled: number; total: number } {
    let filled = 0;
    let total = 0;
    for (const field of countableFields(subtab, values)) {
        total += 1;
        if (fieldIsFilled(field, values)) filled += 1;
    }
    return { filled, total };
}

// Required-only completion, mirroring Common App: a section's green check
// depends on required questions alone — optional fields never block it, and a
// subtab with no visible required fields counts as complete. CoursesGrades
// widgets stay in the check despite not being flagged required, so a
// started-but-unfinished course still blocks the checkmark.
export function subtabRequiredComplete(subtab: FillAppSubtab, values: FillAppValues): boolean {
    return countableFields(subtab, values)
        .filter(isCompletionField)
        .every(f => fieldIsFilled(f, values));
}

export function screenRequiredComplete(screen: FillAppScreen, values: FillAppValues): boolean {
    return screen.subtabs.every(st => !subtabIsVisible(st, values) || subtabRequiredComplete(st, values));
}

// ---- Card-group helpers --------------------------------------------------

export type GroupState = 'complete' | 'in-progress' | 'empty';

// Fallback when a cached schema predates groups: the whole subtab as one card.
export function subtabGroups(subtab: FillAppSubtab): FillAppFieldGroup[] {
    if (subtab.groups && subtab.groups.length > 0) return subtab.groups;
    return [{ id: `g-${subtab.id}-all`, title: subtab.label, qids: subtab.fields.map(f => f.qid) }];
}

// Fields of one card group, in schema order.
export function groupFieldsOf(subtab: FillAppSubtab, group: FillAppFieldGroup): FillAppField[] {
    const member = new Set(group.qids);
    return subtab.fields.filter(f => member.has(f.qid));
}

// Presentational card state. Untouched groups read "empty" even when they have
// no required fields — a green check on a never-opened card would be noise.
// Progress percentages elsewhere keep their own required-only semantics.
export function groupState(
    subtab: FillAppSubtab,
    group: FillAppFieldGroup,
    values: FillAppValues,
): GroupState {
    const member = new Set(group.qids);
    const fields = countableFields(subtab, values).filter(f => member.has(f.qid));
    const filled = fields.filter(f => fieldIsFilled(f, values));
    if (filled.length === 0) return 'empty';
    const gating = fields.filter(isCompletionField);
    return gating.every(f => fieldIsFilled(f, values)) ? 'complete' : 'in-progress';
}


// A bare value tells the student nothing when the answer is Yes/No — a
// collapsed card reading "No · Divorced · Parent 1" gives no clue which
// question each part answered. Prefix those with a trimmed question label.
function summaryShortLabel(field: FillAppField): string {
    let t = (field.label || '').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    t = t.replace(/\s*\*$/, '').replace(/[?:.]$/, '');
    if (t.length > 26) t = `${t.slice(0, 26).trimEnd()}…`;
    return t;
}

function summaryValue(field: FillAppField, values: FillAppValues, choiceGroups: ChoiceGroups): string {
    const raw = values[field.qid];
    if (field.type === 'CoursesGrades') return 'Transcript in progress';
    if (Array.isArray(raw)) {
        const labels = raw.slice(0, 2).map(v => choiceLabel(choiceGroups[field.choiceGroup || ''], v) || String(v));
        return labels.join(', ') + (raw.length > 2 ? ', …' : '');
    }
    if (raw != null && typeof raw === 'object') {
        // Address-style objects: prefer "City, State", else first non-empty part.
        const o = raw as Record<string, unknown>;
        const city = String(o.city ?? '').trim();
        const state = String(o.state ?? '').trim();
        if (city) return state ? `${city}, ${state}` : city;
        const part = Object.values(o).find(p => typeof p === 'string' && p.trim() !== '');
        return part ? String(part) : '';
    }
    const viaChoice = field.choiceGroup ? choiceLabel(choiceGroups[field.choiceGroup], raw) : '';
    let s = (viaChoice || String(raw ?? '')).trim();
    if (/^(yes|no)$/i.test(s)) {
        const q = summaryShortLabel(field);
        if (q) s = `${q}: ${s}`;
    }
    return s.length > 42 ? `${s.slice(0, 42).trimEnd()}…` : s;
}

// One-line summary for a collapsed card: the first few filled answers.
export function groupSummary(
    subtab: FillAppSubtab,
    group: FillAppFieldGroup,
    values: FillAppValues,
    choiceGroups: ChoiceGroups,
): string {
    const member = new Set(group.qids);
    const parts: string[] = [];
    for (const field of countableFields(subtab, values)) {
        if (!member.has(field.qid) || !fieldIsFilled(field, values)) continue;
        const text = summaryValue(field, values, choiceGroups);
        if (text) parts.push(text);
        if (parts.length === 3) break;
    }
    const line = parts.join(' · ');
    return line.length > 90 ? `${line.slice(0, 90).trimEnd()}…` : line;
}

// Open (unresolved) comment count for a group, from a qid→count record.
export function groupCommentCount(group: FillAppFieldGroup, countsByQid: Record<string, number>): number {
    return group.qids.reduce((n, qid) => n + (countsByQid[qid] || 0), 0);
}

export function computeOverallProgress(
    schema: FillAppSchema,
    values: FillAppValues,
): { filled: number; total: number; percent: number; sectionsStarted: number } {
    let filled = 0;
    let total = 0;
    let sectionsStarted = 0;
    for (const screen of schema.screens) {
        const r = computeScreenProgress(screen, values);
        filled += r.filled;
        total += r.total;
        // "Started" stays any-field-based: it measures activity, while
        // filled/total/percent measure required-field completion.
        const started = screen.subtabs.some(st =>
            subtabIsVisible(st, values) &&
            countableFields(st, values).some(f => fieldIsFilled(f, values)));
        if (started) sectionsStarted += 1;
    }
    return { filled, total, percent: completionPercent(filled, total), sectionsStarted };
}

export function countFilledFields(schema: FillAppSchema, values: FillAppValues): number {
    const known = new Set<string>();
    for (const screen of schema.screens) {
        for (const qid of screenQids(screen)) known.add(qid);
    }
    let n = 0;
    for (const [qid, v] of Object.entries(values)) {
        if (!known.has(qid)) continue;
        if (!isEmpty(v)) n += 1;
    }
    return n;
}

// ---- Field comments (tutor feedback on individual form fields) -------------

export interface FillAppFieldComment {
    id: number;
    qid: string;
    body: string;
    authorTutorId: number | null;
    authorName: string | null;
    isResolved: boolean;
    resolvedAt: string | null;
    valueSnapshot: unknown;
    resolvedValueSnapshot: unknown;
    createdAt: string;
    modifiedAt: string;
}

// qid → owning screen/subtab, for sidebar badge counts and the hidden-field
// banner. subtab.fields includes repeating-group member qids, so this is a
// flat sweep — no group handling needed.
export function buildQidLocationMap(
    schema: FillAppSchema,
): Map<string, { screenId: string; subtabId: string }> {
    const map = new Map<string, { screenId: string; subtabId: string }>();
    for (const screen of schema.screens) {
        for (const subtab of screen.subtabs) {
            for (const field of subtab.fields) {
                map.set(field.qid, { screenId: screen.id, subtabId: subtab.id });
            }
        }
    }
    return map;
}

// The qids a subtab currently renders: mirrors FillAppSubtab/RepeatingGroup
// gating (field visibility + active group slots). Used to detect comments on
// fields hidden by conditional answers.
export function visibleQidsOfSubtab(subtab: FillAppSubtab, values: FillAppValues): Set<string> {
    const { all, active } = partitionGroupQids(subtab, values);
    const out = new Set<string>();
    for (const field of subtab.fields) {
        if (field.type === 'Label') continue;
        if (all.has(field.qid)) {
            if (!active.has(field.qid) || !fieldIsVisible(field, values)) continue;
        } else if (!fieldIsVisible(field, values)) {
            continue;
        }
        out.add(field.qid);
    }
    return out;
}
